import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AppMessageService } from '../../../../../../../shared/services/app-message/app-message.service';
import { combineLatest, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { DateFormatPipe } from '../../../../../../../shared/pipes/date.pipe';
import { EmailTemplateService } from '../../../../../../definition/email-template/services/email-template.service';
import { selectedCase, selectPatientFullname } from '../../../../../store/selectors/patient-informations.selector';
import { selectCaseDate } from '../../../../../store/selectors/common-case.selector';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CaseService } from '../../../../../services/case.services';

interface EmailTemplate {
  id: number;
  name: string;
  content: string;
  emailTitle: string;
  toEmailAddresses?: string[];
  toCCAddresses?: string[];
  toBCCAddresses?: string[];
}

@Component({
  selector: 'app-case-send-email',
  templateUrl: './case-send-email.component.html',
  styleUrl: './case-send-email.component.scss',
})
export class CaseSendEmailComponent implements OnInit, OnDestroy {
  constructor(
    private readonly emailTemplateService: EmailTemplateService,
    private readonly messageService: AppMessageService,
    private readonly caseService: CaseService,
    private readonly store: Store,
    private readonly datePipe: DateFormatPipe,
    private readonly sanitizer: DomSanitizer
  ) {}

  showPopup: boolean = false;
  emailTemplates: EmailTemplate[] = [];
  filteredTemplates:EmailTemplate[] = [];
  selectedTemplate: EmailTemplate | null = null;
  receiverEmails: string[] = [];
  ccEmails: string[] = [];
  bccEmails: string[] = [];
  patientEmails: string[] = [];
  selectedReceiverEmails: string[] = [];
  selectedCCEmails: string[] = [];
  selectedBCCEmails: string[] = [];
  selectedContent = '';
  sanitizedContent: SafeHtml;
  readonly MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB in bytes
  readonly ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/octet-stream',
    'text/plain',
  ];
  dataMap = {
    'Ad Soyad': '',
    'Vaka Tarihi': '',
  };
  errorMessage: string = '';

  private subscription!: Subscription;

  files: File[] = [];

  caseId: number;
  ngOnInit(): void {
     this.emailTemplateService.getAllFilter({}, {
      'filter.isActive': true,
      'filter.getAll':true

     }).subscribe({
      next: (res) => {
         this.emailTemplates = res?.items ?? [];
      },
    });

    this.subscription = combineLatest([
      this.store.select(selectPatientFullname),
      this.store.select(selectCaseDate),
      this.store.select(selectedCase),
    ]).subscribe({
      next: ([selectPatientFullname, selectCaseDate, selectedCase]) => {
        this.dataMap = {
          ...this.dataMap,
          'Ad Soyad':
            selectPatientFullname?.firstName +
            ' ' +
            selectPatientFullname?.lastName,
          'Vaka Tarihi': selectCaseDate
            ? this.datePipe
                .transform(new Date(selectCaseDate), true)
                .split(' ')[1] ?? '[Vaka Tarihi]'
            : '[Vaka Tarihi]',
        };
        
      this.caseId = Number(selectedCase)
      },
    });
  }

  onTemplateChange(event: Event) {
    this.selectedReceiverEmails = [];
    this.selectedCCEmails = [];
    this.selectedBCCEmails = [];
    if (this.selectedTemplate) {
      this.receiverEmails = [
        ...(this.selectedTemplate.toEmailAddresses || []),
        ...this.patientEmails,
      ];
      this.bccEmails = [...(this.selectedTemplate.toBCCAddresses || [])];
      this.ccEmails = [...(this.selectedTemplate.toCCAddresses || [])];
      this.selectedContent = this.selectedTemplate.content;
      this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(
        this.selectedContent
      );
      for (const [key, value] of Object.entries(this.dataMap)) {
        this.selectedContent = this.selectedContent.replaceAll(`[${key}]`, value);
      }
    } else {
      this.selectedReceiverEmails = [];
      this.selectedBCCEmails = [];
      this.selectedCCEmails = [];
    }
  }

  initilize(emails: any[],caseTypeId:number|null, recordType?:string|null) {
    this.showPopup = true;
    // this.filteredTemplates=this.emailTemplates?.filter((t:any)=>t?.caseTypeId?.some((c:any)=>c?.incrementId===caseTypeId)) ?? []
    this.filteredTemplates = this.emailTemplates?.filter((t: any) => {
      const hasCaseType = t?.caseTypeId?.some((c: any) => c?.incrementId === caseTypeId);
      const hasRecordType = t?.recordTypes?.includes(recordType);
      return hasCaseType && hasRecordType;
    }) ?? [];

    this.patientEmails = emails;
  }

  isReceiverEmailSelected(email: string): boolean {
    return this.selectedReceiverEmails.includes(email);
  }
  isCCEmailSelected(email: string): boolean {
    return this.selectedCCEmails.includes(email);
  }
  isBCCEmailSelected(email: string): boolean {
    return this.selectedBCCEmails.includes(email);
  }

  toggleReceiverEmail(email: string) {
    if (this.isReceiverEmailSelected(email)) {
      this.selectedReceiverEmails = this.selectedReceiverEmails.filter(
        (p) => p !== email
      );
    } else {
      this.selectedReceiverEmails = [...this.selectedReceiverEmails, email];

    }
  }

  toggleCCEmail(email: string) {
    if (this.isCCEmailSelected(email)) {
      this.selectedCCEmails = this.selectedCCEmails.filter((p) => p !== email);

    } else {
      this.selectedCCEmails = [...this.selectedCCEmails, email];
    }
  }
  toggleBCCEmail(email: string) {
    if (this.isBCCEmailSelected(email)) {
      this.selectedBCCEmails = this.selectedBCCEmails.filter(
        (p) => p !== email
      );
    } else {
      
      this.selectedBCCEmails = [...this.selectedBCCEmails, email];
    }
  }

  sendEmail() {
    if (this.selectedReceiverEmails.length === 0) {
      this.messageService.warningToast('', 'Lütfen en az bir email giriniz.');
      return;
    }
    const formData = new FormData();
    for (const file of Array.from(this.files)) {
      formData.append('images', file); // aynı isimle çoklu gönderim
    }


    const updatedHtml = this.selectedContent.replaceAll(
      /<img\s+src="data:image\/[^;]+;base64,([^"]+)"\s*\/?>/g,
      (_, base64, index) => {

        const byteString = atob(base64);
        const arrayBuffer = new Uint8Array(byteString.length);

        let bufferIndex = 0;
        for (const char of byteString) {
          const codePoint = char.codePointAt(0)!;
          arrayBuffer[bufferIndex++] = codePoint;
        }

        const blob = new Blob([arrayBuffer], { type: 'image/png' });
        const file = new File([blob], `inline-image-${index}.png`, {
          type: 'image/png',
        });

        formData.append('images', file);
        return ``;
      }
    );
    formData.append('html', updatedHtml);
    formData.append('subject', this.selectedTemplate!.emailTitle ?? '');
    formData.append('to', JSON.stringify(this.selectedReceiverEmails));
    formData.append('bcc', JSON.stringify(this.selectedBCCEmails));
    formData.append('cc', JSON.stringify(this.selectedCCEmails));
    // return;
    this.caseService.sendEmail(formData, this.caseId).subscribe((res) => {
      this.showPopup = false;
      this.messageService.successToast(
        '',
        'Email gönderildi.'
      );
    });
  }

  handleChipAdd(event: any, input: 'receiver' | 'cc' | 'bcc') {
    const email = event.value.trim().replaceAll(/\s+/g, ''); // boşlukları temizle
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      switch (input) {
        case 'receiver':
          this.selectedReceiverEmails = this.selectedReceiverEmails.filter(
            (e) => e !== email
          );
          break;
        case 'bcc':
          this.selectedBCCEmails = this.selectedBCCEmails.filter(
            (e) => e !== email
          );
          break;
        case 'cc':
          this.selectedCCEmails = this.selectedCCEmails.filter(
            (e) => e !== email
          );
          break;
      }
    }
  }

  clearAll() {
    this.selectedTemplate = null;
    this.receiverEmails = [];
    this.ccEmails = [];
    this.bccEmails = [];
    this.patientEmails = [];
    this.selectedTemplate = null;
    this.selectedReceiverEmails = [];
    this.selectedCCEmails = [];
    this.selectedBCCEmails = [];
    this.selectedContent = '';
    this.sanitizedContent = '';
  }

  handleNewFiles(newFiles: File[]) {
    this.errorMessage = '';

    const allFiles = [...this.files, ...newFiles];
    const totalSize = allFiles.reduce((sum, file) => sum + file.size, 0);

    const invalidTypeFile = newFiles.find(
      (file) => !this.ALLOWED_TYPES.includes(file.type)
    );
    if (invalidTypeFile) {
      this.errorMessage = `File type not allowed: ${invalidTypeFile.name}`;
      return;
    }

    if (totalSize > this.MAX_TOTAL_SIZE) {
      this.errorMessage = 'Total file size exceeds 20MB limit';
      return;
    }

    this.files = allFiles;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onFileSelected(event: any) {
    const incomingFiles = Array.from(event.target.files);
    this.handleNewFiles(incomingFiles as File[]);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const incomingFiles = Array.from(event.dataTransfer?.files ?? []);
    this.handleNewFiles(incomingFiles);
  }

  addFiles(newFiles: File[]) {
    this.files = [...this.files, ...newFiles];
  }

  removeFile(index: number) {
    this.files = this.files.filter((_, i) => i !== index);
    this.errorMessage = '';
  }

  getTotalSizeInBytes(): number {
    return this.files.reduce((total, file) => total + file.size, 0);
  }

  getTotalSize(): string {
    return (this.getTotalSizeInBytes() / 1024 / 1024).toFixed(2);
  }
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event): void {
    this.unSub();
  }

  unSub() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unSub();
  }
}
